import React from "react";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { BuildingIcon, KeyIcon } from "./gateIcons";
import OrganizationReviewCard from "./OrganizationReviewCard";
import { logout, startPortalOnboarding } from "../../setup/auth/oidc";

/**
 * Shown by the gate to a signed-in user who has no organization (a customer who came through
 * single sign-on). "Continue" starts the Waffy ID onboarding page (portal login + organization);
 * "No" saves nothing: the user stays signed in and can go back to the Waffy app or sign out.
 */
const PortalOnboardingPrompt: React.FC = () => {
  const { t } = useTranslation();
  const userAppUrl = process.env.REACT_APP_URL?.trim();

  // What the Waffy ID onboarding page will ask for, in the pending screen's row style.
  const steps = [
    {
      key: "login",
      icon: <KeyIcon />,
      title: t("lbl_portal_onboarding_step_login"),
      subtitle: t("lbl_portal_onboarding_step_login_desc"),
    },
    {
      key: "organization",
      icon: <BuildingIcon />,
      title: t("lbl_portal_onboarding_step_org"),
      subtitle: t("lbl_portal_onboarding_step_org_desc"),
    },
  ];

  return (
    <OrganizationReviewCard
      variant="onboarding"
      title={t("lbl_portal_onboarding_title")}
      description={t("lbl_portal_onboarding_body")}
      descriptionClassName={styles.reviewReason}>
      <div className={styles.onboardingSteps}>
        {steps.map(({ key, icon, title, subtitle }) => (
          <div className={styles.dcRow} key={key}>
            <span className={styles.dcIcon}>{icon}</span>
            <span className={styles.dcTxt}>
              <b>{title}</b>
              <span className={styles.dcFile}>{subtitle}</span>
            </span>
          </div>
        ))}
      </div>
      <div className={styles.reviewAction}>
        <button type="button" onClick={() => startPortalOnboarding()}>
          {t("lbl_portal_onboarding_continue")}
        </button>
      </div>
      <div className={styles.reviewSep} />
      <div className={styles.reviewFoot}>
        {userAppUrl && (
          <>
            <a className={styles.reviewFootLink} href={userAppUrl}>
              {t("lbl_portal_onboarding_go_to_app")}
            </a>
            <span className={styles.dotSep}>·</span>
          </>
        )}
        <button
          type="button"
          className={styles.reviewFootLink}
          onClick={() => logout()}>
          {t("lbl_portal_onboarding_sign_out")}
        </button>
      </div>
    </OrganizationReviewCard>
  );
};

export default PortalOnboardingPrompt;
